import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  ListGroup,
  ListGroupItem,
  Row,
  Card,
  CardHeader,
  Col,
  Input,
  Button,
  CardBody,
} from "reactstrap";
import { toast, ToastContainer } from 'react-toastify';
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import DeleteModal from "../../../Components/Common/DeleteModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { Link } from "react-router-dom";
import api, { handleApiError } from "../../../helpers/api";
import Select from "react-select";
import { useTranslation } from 'react-i18next';

const EcommerceProducts = ({ navigate }) => {
  const { t } = useTranslation();
  document.title = t("ecommerce_products.title");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productList, setProductList] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [cate, setCate] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [inStock, setInStock] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState({});
  const [selectedBrand, setSelectedBrand] = useState("");
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExportCSV, setIsExportCSV] = useState(false);

  // Fetch categories and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoryRes, productRes] = await Promise.all([
          api.get('/categories'),
          api.get('/products')
        ]);
        setCategories(categoryRes.data.data);
        setProducts(productRes.data.data);
        setProductList(productRes.data.data);
        setFilteredProducts(productRes.data.data);
      } catch (error) {
        handleApiError(error, () => {}, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_products.toasts.fetch_failed"));
      }
    };
    fetchData();
  }, [navigate, t]);

  // Build category tree
  const categoryTree = useMemo(() => {
    const tree = [];
    const categoryMap = new Map(categories.map(cat => [cat._id, { ...cat, children: [] }]));

    categoryMap.forEach(category => {
      if (category.parent) {
        const parent = categoryMap.get(category.parent._id);
        if (parent) parent.children.push(category);
      } else {
        tree.push(category);
      }
    });

    return tree;
  }, [categories]);

  // Get unique brands
  const brandOptions = useMemo(() => {
    const brands = [...new Set(productList.map(product => product.brand).filter(Boolean))];
    return brands.map(brand => ({ value: brand, label: brand }));
  }, [productList]);

  // Filter products (API-based filters)
  useEffect(() => {
    const fetchFilteredProducts = async () => {
      try {
        const queryParams = {};
        if (cate !== "all") queryParams.category = cate;
        if (priceRange[0] > 0) queryParams.minPrice = priceRange[0];
        if (priceRange[1] < 2000) queryParams.maxPrice = priceRange[1];
        if (inStock) queryParams.inStock = true;
        if (Object.keys(selectedSpecs).length > 0) {
          queryParams.specs = Object.entries(selectedSpecs)
              .filter(([_, value]) => value)
              .map(([key, value]) => `${key}=${value}`)
              .join(",");
        }
        if (selectedBrand) queryParams.brand = selectedBrand;

        const response = await api.get('/products', { params: queryParams });
        setProducts(response.data.data);
        setProductList(response.data.data);
        setFilteredProducts(response.data.data);
      } catch (error) {
        handleApiError(error, () => {}, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_products.toasts.fetch_products_failed"));
      }
    };
    fetchFilteredProducts();
  }, [cate, priceRange, inStock, selectedSpecs, selectedBrand, navigate, t]);

  // Client-side search filter
  useEffect(() => {
    if (searchQuery) {
      const filtered = productList.filter(product =>
          product.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(productList);
    }
  }, [searchQuery, productList]);

  // Category filter
  const handleCategoryChange = (categoryId) => {
    setCate(categoryId);
    setSelectedSpecs({});
  };

  // Price filter
  const onUpdatePrice = (value) => {
    setPriceRange(value);
  };

  // Stock filter
  const toggleStockFilter = () => {
    setInStock(prev => !prev);
  };

  // Specs filter
  const handleSpecChange = (specName, option, isMulti) => {
    setSelectedSpecs((prev) => {
      if (isMulti) {
        const currentValues = prev[specName] ? prev[specName].split(',') : [];
        if (option) {
          if (Array.isArray(option)) {
            const newValues = option.map(opt => opt.value);
            return {
              ...prev,
              [specName]: newValues.join(',') || undefined,
            };
          } else {
            if (currentValues.includes(option.value)) {
              return {
                ...prev,
                [specName]: currentValues.filter(val => val !== option.value).join(',') || undefined,
              };
            } else {
              return {
                ...prev,
                [specName]: [...currentValues, option.value].join(','),
              };
            }
          }
        } else {
          return { ...prev, [specName]: undefined };
        }
      } else {
        return {
          ...prev,
          [specName]: option ? option.value : undefined,
        };
      }
    });
  };

  // Brand filter
  const handleBrandChange = (option) => {
    setSelectedBrand(option ? option.value : "");
  };

  // Search handler
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value || "");
  };

  // Delete product
  const handleDeleteProduct = async () => {
    if (productToDelete) {
      setIsDeleting(true);
      try {
        await api.delete(`/products/${productToDelete._id}`);
        setProducts(products.filter(p => p._id !== productToDelete._id));
        setProductList(productList.filter(p => p._id !== productToDelete._id));
        setFilteredProducts(filteredProducts.filter(p => p._id !== productToDelete._id));
        setDeleteModal(false);
        toast.success(t("ecommerce_products.toasts.delete_success"));
      } catch (error) {
        handleApiError(error, () => {}, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_products.toasts.delete_failed"));
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Delete multiple products
  const deleteMultiple = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(selectedProducts.map(id => api.delete(`/products/${id}`)));
      setProducts(products.filter(p => !selectedProducts.includes(p._id)));
      setProductList(productList.filter(p => !selectedProducts.includes(p._id)));
      setFilteredProducts(filteredProducts.filter(p => !selectedProducts.includes(p._id)));
      setSelectedProducts([]);
      setDeleteModalMulti(false);
      toast.success(t("ecommerce_products.toasts.delete_multiple_success"));
    } catch (error) {
      handleApiError(error, () => {}, navigate);
      toast.error(error.response?.data?.message || t("ecommerce_products.toasts.delete_multiple_failed"));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle checkbox selection
  const handleCheckboxChange = (id) => {
    setSelectedProducts(prev =>
        prev.includes(id)
            ? prev.filter(pId => pId !== id)
            : [...prev, id]
    );
  };

  // Prepare CSV data
  const csvData = useMemo(() => (
      filteredProducts.map(product => ({
        Name: product.name,
        Brand: product.brand,
        Category: product.category?.name || "",
        Price: `${product.price?.toFixed(2)} TND`,
        Stock: product.stock,
        ...Object.fromEntries(
            Object.entries(product.specs || {}).map(([key, value]) => [
              `Spec_${key}`,
              Array.isArray(value) ? value.map(v => v.name || v).join(', ') : value
            ])
        ),
      }))
  ), [filteredProducts]);

  // Render category tree
  const renderCategoryMenu = (categories, level = 0) => {
    return categories.map(category => (
        <React.Fragment key={category._id}>
          <ListGroupItem
              action
              active={cate === category._id}
              onClick={() => handleCategoryChange(category._id)}
              className="py-2 border-bottom border-light hover:bg-gray-100 cursor-pointer transition-colors"
              style={{ paddingLeft: `${level * 20 + 10}px` }}
          >
            <span className="text-sm font-medium text-gray-700">{category.name}</span>
          </ListGroupItem>
          {category.children.length > 0 && renderCategoryMenu(category.children, level + 1)}
        </React.Fragment>
    ));
  };

  // Get specs for selected category
  const selectedCategory = categories.find(cat => cat._id === cate) || {};
  const specOptions = selectedCategory.possibleSpecs || [];

  const columns = useMemo(() => [
    {
      Header: t("ecommerce_products.table.columns.checkbox"),
      Cell: ({ row }) => (
          <input
              type="checkbox"
              className="productCheckBox form-check-input"
              value={row.original._id}
              checked={selectedProducts.includes(row.original._id)}
              onChange={() => handleCheckboxChange(row.original._id)}
          />
      ),
    },
    {
      Header: t("ecommerce_products.table.columns.product"),
      Cell: ({ row }) => (
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0 me-3">
              <div className="avatar-sm bg-light rounded p-1">
                <img
                    src={row.original.images?.[0]?.url || "placeholder.jpg"}
                    alt=""
                    className="img-fluid d-block"
                />
              </div>
            </div>
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                <Link to={`/apps-ecommerce-product-details/${row.original._id}`} className="text-dark">
                  {row.original.name}
                </Link>
              </h5>
              <p className="text-muted mb-0">
                {t("ecommerce_products.table.category")}: <span className="fw-medium">{row.original.category?.name}</span>
              </p>
            </div>
          </div>
      ),
    },
    {
      Header: t("ecommerce_products.table.columns.stock"),
      accessor: "stock",
      filterable: false,
    },
    {
      Header: t("ecommerce_products.table.columns.price"),
      accessor: "price",
      filterable: false,
      Cell: ({ value }) => `${value.toFixed(2)} TND`,
    },
    {
      Header: t("ecommerce_products.table.columns.action"),
      Cell: ({ row }) => (
          <UncontrolledDropdown>
            <DropdownToggle href="#" className="btn-soft-secondary btn-sm" tag="button">
              <i className="ri-more-fill" />
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem to={`/apps-ecommerce-edit-product/${row.original._id}`}>
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> {t("ecommerce_products.actions.edit")}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                  href="#"
                  onClick={() => {
                    setProductToDelete(row.original);
                    setDeleteModal(true);
                  }}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> {t("ecommerce_products.actions.delete")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
      ),
    },
  ], [selectedProducts, t]);

  // Custom search input
  const CustomGlobalFilter = ({ globalFilter, setGlobalFilter }) => {
    return (
        <Input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t("ecommerce_products.search_placeholder")}
            className="form-control"
            style={{ width: '300px', height: '40px', fontSize: '16px' }}
        />
    );
  };

  return (
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <ExportCSVModal
            show={isExportCSV}
            onCloseClick={() => setIsExportCSV(false)}
            data={csvData}
            filename="products.csv"
        />
        <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteProduct}
            onCloseClick={() => setDeleteModal(false)}
            deleteButtonDisabled={isDeleting}
        />
        <DeleteModal
            show={deleteModalMulti}
            onDeleteClick={deleteMultiple}
            onCloseClick={() => setDeleteModalMulti(false)}
            deleteButtonDisabled={isDeleting}
        />
        <Container fluid>
          <BreadCrumb title={t("ecommerce_products.title")} pageTitle={t("ecommerce_products.page_title")} />
          <Row>
            <Col xl={3} lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="fs-16">{t("ecommerce_products.filters.title")}</h5>
                  {selectedCategory._id && (
                      <p className="text-muted fs-12">
                        {t("ecommerce_products.filters.category_type")}: {selectedCategory.customizable ? t("ecommerce_products.filters.customizable") : t("ecommerce_products.filters.non_customizable")}
                      </p>
                  )}
                </CardHeader>
                <ListGroup flush>
                  <ListGroupItem>
                    <div className="d-flex mb-2 align-items-center">
                      <div className="flex-grow-1">
                        <p className="text-muted text-uppercase fs-12 fw-medium mb-0">{t("ecommerce_products.filters.categories")}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <Link
                            to="#"
                            className="text-decoration-underline text-sm"
                            onClick={() => {
                              setCate("all");
                              setPriceRange([0, 2000]);
                              setInStock(false);
                              setSelectedSpecs({});
                              setSelectedBrand("");
                              setSearchQuery("");
                            }}
                        >
                          {t("ecommerce_products.filters.clear_all")}
                        </Link>
                      </div>
                    </div>
                    <ListGroupItem
                        action
                        active={cate === "all"}
                        onClick={() => handleCategoryChange("all")}
                        className="py-2 border-bottom border-light hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-700">{t("ecommerce_products.filters.all_categories")}</span>
                    </ListGroupItem>
                    {renderCategoryMenu(categoryTree)}
                  </ListGroupItem>
                  <ListGroupItem>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2 mt-3">{t("ecommerce_products.filters.stock")}</p>
                    <div className="form-check">
                      <input
                          className="form-check-input"
                          type="checkbox"
                          id="inStockFilter"
                          checked={inStock}
                          onChange={toggleStockFilter}
                      />
                      <label className="form-check-label text-sm text-gray-700" htmlFor="inStockFilter">
                        {t("ecommerce_products.filters.in_stock")}
                      </label>
                    </div>
                  </ListGroupItem>
                  <ListGroupItem>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2 mt-3">{t("ecommerce_products.filters.price")}</p>
                    <Nouislider
                        range={{ min: 0, max: 2000 }}
                        start={priceRange}
                        connect
                        onSlide={onUpdatePrice}
                        data-slider-color="primary"
                        id="product-price-range"
                    />
                    <div className="formCost d-flex gap-2 align-items-center mt-3">
                      <input className="form-control form-control-sm text-sm" type="text" value={`${priceRange[0]} TND`} readOnly />
                      <span className="fw-semibold text-muted text-sm">{t("ecommerce_products.filters.to")}</span>
                      <input className="form-control form-control-sm text-sm" type="text" value={`${priceRange[1]} TND`} readOnly />
                    </div>
                  </ListGroupItem>
                  {specOptions.length > 0 && (
                      <ListGroupItem>
                        <p className="text-muted text-uppercase fs-12 fw-medium mb-2 mt-3">{t("ecommerce_products.filters.specifications")}</p>
                        {specOptions.map((spec) => (
                            <div key={spec.name} className="mb-3">
                              <label className="text-sm font-medium text-gray-700 mb-1">{spec.name}</label>
                              <Select
                                  options={spec.options.map((opt) => ({ value: opt.value, label: opt.value }))}
                                  placeholder={t("ecommerce_products.filters.select_spec", { name: spec.name })}
                                  onChange={(option) => handleSpecChange(spec.name, option, spec.multiple)}
                                  isClearable
                                  isMulti={spec.multiple}
                                  className="text-sm"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      borderColor: '#e2e8f0',
                                      boxShadow: 'none',
                                      '&:hover': { borderColor: '#cbd5e1' },
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    option: (base, { isFocused }) => ({
                                      ...base,
                                      backgroundColor: isFocused ? '#f1f5f9' : 'white',
                                      color: '#1f2937',
                                    }),
                                  }}
                              />
                            </div>
                        ))}
                      </ListGroupItem>
                  )}
                  <ListGroupItem>
                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2 mt-3">{t("ecommerce_products.filters.brands")}</p>
                    <Select
                        options={brandOptions}
                        placeholder={t("ecommerce_products.filters.select_brand")}
                        onChange={handleBrandChange}
                        isClearable
                        className="text-sm"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: '#e2e8f0',
                            boxShadow: 'none',
                            '&:hover': { borderColor: '#cbd5e1' },
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                          option: (base, { isFocused }) => ({
                            ...base,
                            backgroundColor: isFocused ? '#f1f5f9' : 'white',
                            color: '#1f2937',
                          }),
                        }}
                    />
                  </ListGroupItem>
                </ListGroup>
              </Card>
            </Col>
            <Col xl={9} lg={8}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="align-items-center gy-3">
                    <Col className="col-sm">
                      <h5 className="card-title mb-0">{t("ecommerce_products.card_title")}({filteredProducts.length})</h5>
                    </Col>
                    <Col className="col-sm-auto">
                      <div className="d-flex flex-row align-items-center gap-2">
                        <div id="selection-element" style={{ display: selectedProducts.length > 0 ? "block" : "none" }}>
                          <div className="my-n1 d-flex align-items-center text-muted">
                            {t("ecommerce_products.selection.select")}{" "}
                            <span id="select-content" className="text-body fw-semibold px-1">
                              {selectedProducts.length}
                            </span>{" "}
                            {t("ecommerce_products.selection.result")}{" "}
                            <button
                                type="button"
                                className="btn btn-link link-danger p-0 ms-3 text-sm"
                                onClick={() => setDeleteModalMulti(true)}
                            >
                              {t("ecommerce_products.selection.remove")}
                            </button>
                          </div>
                        </div>
                        <Input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearch}
                            placeholder={t("ecommerce_products.search_placeholder")}
                            className="form-control"
                            style={{ width: '300px', height: '40px', fontSize: '16px' }}
                        />
                        <Link to="/apps-ecommerce-add-product">
                          <Button
                              color="primary"
                              className="btn btn-success add-btn"
                              style={{ width: '150px', height: '40px', fontSize: '16px' }}
                          >
                            <i className="ri-add-line align-bottom me-1"></i> {t("ecommerce_products.actions.add_product")}
                          </Button>
                        </Link>
                        <Button
                            color="info"
                            className="btn btn-info"
                            style={{ width: '150px', height: '40px', fontSize: '16px' }}
                            onClick={() => setIsExportCSV(true)}
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i> {t("ecommerce_products.actions.export")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  {filteredProducts.length > 0 ? (
                      <TableContainer
                          columns={columns}
                          data={filteredProducts}
                          isGlobalFilter={false}
                          isAddUserList={false}
                          customPageSize={6}
                          divClass="table-responsive mb-1"
                          tableClass="mb-0 align-middle table-borderless"
                          theadClass="table-light text-muted"
                          isProductsFilter={false}
                          customGlobalFilter={CustomGlobalFilter}
                      />
                  ) : (
                      <div className="py-4 text-center">
                        <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="primary:#405189,secondary:#0ab39c"
                            style={{ width: "72px", height: "72px" }}
                        ></lord-icon>
                        <div className="mt-4">
                          <h5>{t("ecommerce_products.no_results")}</h5>
                        </div>
                      </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
  );
};

export default EcommerceProducts;